// Blocking overlay shown at the pack bench while a carrier booking is
// in flight — and, on failure, the reason the order is blocked.
// Nothing ships without a booked label (per Jack, 2026-08-05), so this
// panel still has no "skip" affordance: the only ways forward are a
// successful retry, a void-and-re-book, fixing the boxes, or flagging
// the order for a supervisor.
//
// It IS dismissible, which is a different thing. Closing it returns the
// packer to the pack screen with the order untouched and unbooked — the
// Complete button re-runs the booking — so they can fix whatever the
// carrier objected to instead of being trapped on a dead-end modal.
// A failed booking used to have exactly one exit, "Retry booking", which
// is useless when the carrier is rejecting the delivery address: the
// address editor lives on the NEXT screen, which needs a booked label to
// reach (2026-08-19). Hence both the close button and the inline address
// editor below.
//
// "Leave it for a supervisor" is no longer the only exit for a box
// mismatch (2026-10-22). The bench auto-books the moment the last unit is
// scanned, so a packer who goes back and adds a box hits
// booking_box_mismatch against their own live label. That is no longer a
// dead end: any packer may void the label and book again for the boxes
// actually on the bench, behind a two-step confirm (nothing is charged
// before hand-over on any carrier). Retry and the address editor are
// meaningless there - the same boxes get the same refusal - so they are
// not rendered for that code. The rebook call itself can be refused
// (already_shipped, shared_consignment, cancel_window_closed, void_failed);
// those arrive as their own codes with a heading, Flag and close, no retry.

import { useEffect, useState } from "react";
import { AlertTriangle, Flag, Loader2, MapPin, RefreshCw, Truck, X } from "lucide-react";
import { useTranslation } from "../../i18n";
import type { DispatchAddress } from "../../types/Dispatch";

export interface CarrierPanelState {
    state: "booking" | "label_pending" | "failed";
    // failure detail (state === 'failed')
    // no_rules | no_match | service_constraints | apc_rejected |
    // apc_unreachable | label_timeout | booking_cancelling |
    // booking_in_progress | wrong_status | label_fetch_failed | ...
    // label_timeout means ONLY that the label poll ran out — every
    // definitive refusal now arrives under its own code.
    code?: string;
    error?: string | null;
    // APC's per-field validation failures (code === 'apc_rejected') —
    // rendered as a fix-it list so the packer can resolve simple data
    // issues (postcode, phone format, …) and retry without flagging
    // the order.
    apcErrors?: { field: string; message: string }[];
    // Which carrier replied (APC / InPost / DPD Poland / Cargus) — names
    // the heading and the "carrier said" line; absent on an older BE (APC).
    carrierName?: string | null;
    // code === "country_not_served": the destination the chosen carrier
    // refused and the countries it does book.
    country?: string | null;
    allowedCountries?: string[];
    trace?: { ruleId: number; name: string; failed: string }[];
    ruleName?: string | null;
    // code === "booking_box_mismatch": what the order is booked for vs
    // what is on the bench. Both are named in the heading, because "one
    // of your boxes has no label" is only actionable with the numbers.
    bookedPieces?: number | null;
    packedBoxes?: number | null;
    // Same refusal, different shape: the bench is packing the right NUMBER
    // of boxes but not the same ones (a found item in a mailer against the
    // booking for the whole order). The weights are what makes that
    // legible, so they travel too.
    declaredKg?: number | null;
    packedKg?: number | null;
    // code === "cancel_window_closed": why the label can no longer be
    // voided ("manifested" | "already_scanned") and the carrier's cut-off.
    reason?: string | null;
    cutoff?: string | null;
}

// InPost RFC 7807 field paths ("shipment.recipient.phone") → plain
// English. Looked up on the LAST path segment so nesting depth doesn't
// matter; unknown ones degrade to the camel-case split below.
const INPOST_FIELD_LABELS: Record<string, string> = {
    firstName: "First name",
    lastName: "Surname",
    email: "Email address",
    phone: "Phone number (must be a real mobile/landline)",
    companyName: "Company name",
    street: "Street",
    houseNumber: "House number",
    flatNumber: "Flat number",
    city: "Town / city",
    postalCode: "Postcode",
    countryCode: "Country",
    pointId: "InPost locker / shop id",
    length: "Length",
    width: "Width",
    height: "Height",
    amount: "Weight / value",
    unit: "Unit",
    dimensions: "Dimensions",
    weight: "Weight",
    customsClearance: "Customs details",
    contents: "Customs contents",
    hsCode: "HS code",
    unitValue: "Unit value",
    quantity: "Quantity",
    valueAddedServices: "Services",
    priority: "Priority",
};

export const friendlyInpostField = (path: string): string => {
    const clean = String(path ?? "").replace(/\[\d+\]/g, "");
    const last = clean.split(".").filter(Boolean).pop() ?? clean;
    const where = /recipient/i.test(clean) ? "Recipient " : /sender/i.test(clean) ? "Sender " : /destination/i.test(clean) ? "Delivery " : "";
    const label = INPOST_FIELD_LABELS[last];
    if (label) return where ? `${where}${label.charAt(0).toLowerCase()}${label.slice(1)}` : label;
    return `${where}${last.replace(/([a-z0-9])([A-Z])/g, "$1 $2")}`.trim();
};

// "Delivery PostalCode" / "DutyItems.DutyItem.Description" → plain
// English the packer recognises. Unknown tokens degrade to a readable
// camel-case split rather than hiding the field.
const APC_FIELD_LABELS: Record<string, string> = {
    CompanyName: "Name / company",
    ContactName: "Contact name",
    AddressLine1: "Address line 1",
    AddressLine2: "Address line 2",
    City: "Town / city",
    County: "County",
    PostalCode: "Postcode",
    CountryCode: "Country",
    CountryCoude: "Country",
    Email: "Email address",
    PhoneNumber: "Phone number",
    MobileNumber: "Mobile number",
    Instructions: "Delivery instructions",
    Reference: "Order reference",
    CollectionDate: "Collection date",
    ReadyAt: "Collection ready-at time",
    ClosedAt: "Collection closed-at time",
    ProductCode: "Service code",
    NumberOfPieces: "Number of boxes",
    Weight: "Weight",
    Length: "Length",
    Width: "Width",
    Height: "Height",
    Value: "Goods value",
    GoodsValue: "Goods value",
    GoodsDescription: "Goods description",
    Description: "Customs description",
    Origin: "Customs origin",
    OriginCountry: "Customs origin country",
    HTSCode: "Customs commodity code",
    UnitValue: "Customs unit value",
    Units: "Customs units",
    EoriNumber: "EORI number",
    Safeplace: "Safeplace",
};

export const friendlyApcField = (raw: string): string => {
    const cleaned = String(raw ?? "").trim();
    if (!cleaned) return "";
    // Last dotted segment carries the field; the prefix carries context.
    const segments = cleaned.split(/[.\s]+/).filter(Boolean);
    const last = segments[segments.length - 1];
    const mapped = APC_FIELD_LABELS[last];
    const context = segments.includes("Delivery") ? "Delivery — " : "";
    if (mapped) return `${context}${mapped}`;
    // Fallback: split camel case ("PostalCode" → "Postal code").
    const split = last.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    return `${context}${split.charAt(0).toUpperCase()}${split.slice(1).toLowerCase()}`;
};

// The BE sends the manifest cut-off as the account's Closed-at, "HH:MM"
// London wall-clock (cancelWindow.js) - shown as sent, absent as a dash.
const formatCutoff = (cutoff?: string | null): string => (cutoff ? String(cutoff) : "-");

type Props = {
    panel: CarrierPanelState;
    onRetry: () => void;
    retrying?: boolean;
    // Void the live label and book again for the boxes on the bench
    // (code === "booking_box_mismatch" only). The panel owns the two-step
    // confirm; this fires once, after the second click.
    onRebook?: () => void;
    rebooking?: boolean;
    // Dismiss the failure and go back to the pack screen. Absent → the
    // panel is not dismissible (the in-flight states never are).
    onClose?: () => void;
    // Hand the order to a supervisor and leave the bench. The pack
    // screen's own Flag button sits in the title bar, which THIS overlay
    // covers — so a fatal booking failure (no_match, not_gb: nothing the
    // packer can retry their way out of) left them with no exit at all
    // but the close button. Flagging is the correct move there.
    onFlag?: () => void;
    // Inline delivery-address editor. `onSave` persists the address and
    // resolves true on success; the panel then retries the booking on
    // the corrected address.
    addressEditor?: {
        address: DispatchAddress;
        onSave: (address: DispatchAddress) => Promise<boolean>;
    };
};

const DispatchBookingStatus = ({ panel, onRetry, retrying, onRebook, rebooking, onClose, onFlag, addressEditor }: Props) => {
    const { t } = useTranslation();
    const [editingAddress, setEditingAddress] = useState(false);
    const [addressDraft, setAddressDraft] = useState<DispatchAddress | null>(null);
    const [savingAddress, setSavingAddress] = useState(false);
    // The confirm is armed for ONE panel object. Any new failure (the rebook
    // itself refused, a fresh booking attempt) arrives as a new object, so the
    // next mismatch never opens already armed - and there is no reset effect
    // to race a fast click.
    const [confirmingPanel, setConfirmingPanel] = useState<CarrierPanelState | null>(null);
    const confirmingRebook = confirmingPanel === panel;

    // Re-seed the draft whenever the editor is opened on a fresh
    // failure, so a second attempt never edits a stale copy.
    useEffect(() => {
        if (!editingAddress) setAddressDraft(null);
    }, [editingAddress]);
    const draft = addressDraft ?? addressEditor?.address ?? null;
    const setField = (key: keyof DispatchAddress, value: string) => {
        if (!draft) return;
        setAddressDraft({ ...draft, [key]: value });
    };
    const saveAddress = async () => {
        if (!addressEditor || !draft || savingAddress) return;
        setSavingAddress(true);
        try {
            const ok = await addressEditor.onSave(draft);
            if (ok) {
                setEditingAddress(false);
                onRetry();
            }
        } finally {
            setSavingAddress(false);
        }
    };

    const failureHeading = () => {
        switch (panel.code) {
            case "no_rules": return t("carrier_booking_no_rules");
            case "no_match": return t("carrier_booking_no_match");
            case "service_constraints":
                return t("carrier_booking_service_constraints", { rule: panel.ruleName ?? "" });
            case "not_gb": return t("carrier_booking_not_gb");
            case "country_not_served":
                return t("carrier_booking_country_not_served", {
                    carrier: panel.carrierName ?? t("carrier_generic"),
                    allowed: (panel.allowedCountries ?? []).join(", ") || "—",
                    country: panel.country ?? "?",
                });
            case "carrier_misconfigured":
                return t("carrier_booking_carrier_misconfigured", { carrier: panel.carrierName ?? t("carrier_generic") });
            case "service_limits":
                return t("carrier_booking_service_limits", { carrier: panel.carrierName ?? t("carrier_generic") });
            case "address_unroutable":
                return t("carrier_booking_address_unroutable", { carrier: panel.carrierName ?? t("carrier_generic") });
            case "label_timeout": return t("carrier_booking_label_timeout");
            case "apc_rejected": return t("carrier_booking_apc_rejected");
            case "carrier_rejected": return t("carrier_booking_carrier_rejected", { carrier: panel.carrierName ?? t("carrier_generic") });
            case "carrier_unreachable": return t("carrier_booking_carrier_unreachable", { carrier: panel.carrierName ?? t("carrier_generic") });
            case "delivery_point_required": return t("carrier_booking_delivery_point_required");
            // The order's label is mid-cancel — resolvable by waiting,
            // unlike the fatal codes above.
            case "booking_cancelling": return t("carrier_booking_cancelling");
            case "booking_in_progress": return t("carrier_booking_in_flight_elsewhere");
            case "booking_box_mismatch":
                // Same count → it isn't "a box with no label", it's a
                // different shipment against an existing booking.
                return panel.bookedPieces != null && panel.bookedPieces === panel.packedBoxes
                    ? t("carrier_booking_box_contents_mismatch", {
                        booked: String(panel.declaredKg ?? "?"),
                        packed: String(panel.packedKg ?? "?"),
                    })
                    : t("carrier_booking_box_mismatch", {
                        booked: String(panel.bookedPieces ?? "?"),
                        packed: String(panel.packedBoxes ?? "?"),
                    });
            case "already_shipped": return t("carrier_booking_already_shipped");
            case "shared_consignment": return t("carrier_booking_shared_consignment");
            case "cancel_window_closed":
                return t("carrier_booking_cancel_window_closed", {
                    carrier: panel.carrierName ?? t("carrier_generic"),
                    reason: panel.reason === "already_scanned"
                        ? t("carrier_booking_cancel_reason_scanned")
                        : t("carrier_booking_cancel_reason_manifested", { cutoff: formatCutoff(panel.cutoff) }),
                });
            case "void_failed":
                return t("carrier_booking_void_failed", { carrier: panel.carrierName ?? t("carrier_generic") });
            case "wrong_status": return t("carrier_booking_wrong_status");
            case "label_fetch_failed": return t("carrier_booking_label_fetch_failed");
            default: return t("carrier_booking_failed");
        }
    };

    // Codes that carry a per-field fix-it list the packer can act on.
    const rejected = panel.code === "apc_rejected" || panel.code === "carrier_rejected" || panel.code === "address_unroutable";
    const apcErrors = rejected ? (panel.apcErrors ?? []) : [];
    const friendlyField = panel.code === "carrier_rejected" ? friendlyInpostField : friendlyApcField;

    // booking_box_mismatch: the live label is fine, the boxes changed. The
    // exits are void-and-re-book or going back to fix the boxes - never
    // retry (same boxes, same refusal) or the address editor.
    const isMismatch = panel.code === "booking_box_mismatch";
    // The rebook call's own refusals. Nothing the packer can retry out of:
    // heading + Flag + close.
    const isRebookRefusal = ["already_shipped", "shared_consignment", "cancel_window_closed", "void_failed"].includes(panel.code ?? "");
    const showRetry = !isMismatch && !isRebookRefusal;
    const packedLabel = String(panel.packedBoxes ?? "?");

    return (
        <div className="modal-overlay dispatch-booking-overlay">
            <div className="modal-card dispatch-booking-card">
                {/* Dismissible on failure only. An in-flight booking must
                    stay blocking — closing it would let the packer carry on
                    while a consignment is being created behind them. */}
                {panel.state === "failed" && onClose && (
                    <button
                        type="button"
                        className="modal-close dispatch-booking-close"
                        onClick={onClose}
                        aria-label={t("close")}
                    >
                        <X size={18} />
                    </button>
                )}
                {panel.state !== "failed" ? (
                    <>
                        <div className="dispatch-booking-spinner" role="status" aria-live="polite">
                            {panel.state === "booking"
                                ? <span className="dispatch-booking-truck"><Truck size={40} strokeWidth={1.5} /></span>
                                : <Loader2 size={40} strokeWidth={1.5} className="admin-integrations-spinner" />}
                        </div>
                        <h3 className="dispatch-booking-title">
                            {panel.state === "booking"
                                ? t("carrier_booking_in_progress")
                                : t("carrier_booking_label_pending")}
                        </h3>
                        <p className="dispatch-booking-hint">
                            {panel.state === "booking"
                                ? t("carrier_booking_in_progress_hint")
                                : t("carrier_booking_label_pending_hint")}
                        </p>
                    </>
                ) : (
                    <>
                        <div className="dispatch-booking-spinner dispatch-booking-spinner--error">
                            <AlertTriangle size={40} strokeWidth={1.5} />
                        </div>
                        <h3 className="dispatch-booking-title">{failureHeading()}</h3>
                        {apcErrors.length > 0 && (
                            <>
                                <ul className="dispatch-booking-field-errors">
                                    {apcErrors.map((e, i) => (
                                        <li key={`${e.field}-${i}`}>
                                            {e.field && <strong>{friendlyField(e.field)}</strong>}
                                            {e.field && e.message ? " — " : ""}
                                            {e.message}
                                        </li>
                                    ))}
                                </ul>
                                <p className="dispatch-booking-hint">{t("carrier_booking_apc_rejected_hint")}</p>
                            </>
                        )}
                        {/* The carrier's own words, ALWAYS — the friendly
                            heading above says what KIND of failure it is,
                            this says what APC actually replied. It used to
                            be suppressed whenever a field list existed,
                            which hid the one line explaining any error the
                            field list doesn't cover. */}
                        {panel.error && (
                            <p className="dispatch-booking-error">
                                <span className="dispatch-booking-error-label">{t("carrier_said")}</span>
                                {panel.error}
                            </p>
                        )}
                        {(panel.trace ?? []).length > 0 && (
                            <div className="dispatch-booking-trace">
                                {(panel.trace ?? []).map(tr => (
                                    <p key={tr.ruleId}>{tr.name}: {tr.failed}</p>
                                ))}
                            </div>
                        )}
                        {showRetry && addressEditor && draft && (editingAddress ? (
                            <div className="dispatch-booking-address">
                                <div className="dispatch-address-form">
                                    <input value={draft.name ?? ""} onChange={e => setField("name", e.target.value)} placeholder={t("name")} />
                                    <input value={draft.line_1 ?? ""} onChange={e => setField("line_1", e.target.value)} placeholder={t("address_line_1")} />
                                    <input value={draft.line_2 ?? ""} onChange={e => setField("line_2", e.target.value)} placeholder={t("address_line_2")} />
                                    <div className="dispatch-booking-address-row">
                                        <input value={draft.city ?? ""} onChange={e => setField("city", e.target.value)} placeholder={t("city")} />
                                        <input value={draft.county ?? ""} onChange={e => setField("county", e.target.value)} placeholder={t("county")} />
                                    </div>
                                    <div className="dispatch-booking-address-row">
                                        <input value={draft.postcode ?? ""} onChange={e => setField("postcode", e.target.value)} placeholder={t("postcode")} />
                                        <input value={draft.country ?? ""} onChange={e => setField("country", e.target.value)} placeholder={t("country")} style={{ maxWidth: 90 }} />
                                    </div>
                                    <div className="dispatch-booking-address-row">
                                        <input value={draft.phone ?? ""} onChange={e => setField("phone", e.target.value)} placeholder={t("phone")} />
                                        <input value={draft.email ?? ""} onChange={e => setField("email", e.target.value)} placeholder={t("email")} />
                                    </div>
                                </div>
                                <div className="dispatch-booking-address-actions">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm dispatch-modal-btn"
                                        onClick={() => setEditingAddress(false)}
                                        disabled={savingAddress}
                                    >
                                        {t("cancel")}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm dispatch-modal-btn"
                                        onClick={saveAddress}
                                        disabled={savingAddress}
                                    >
                                        {savingAddress && <Loader2 size={16} className="admin-integrations-spinner" />}
                                        {t("carrier_booking_save_address_retry")}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm dispatch-modal-btn dispatch-booking-address-btn"
                                onClick={() => setEditingAddress(true)}
                            >
                                <MapPin size={14} />
                                {t("carrier_booking_edit_address")}
                            </button>
                        ))}
                        {showRetry && <p className="dispatch-booking-hint">{t("carrier_booking_blocked_hint")}</p>}
                        {isMismatch && confirmingRebook && (
                            <p className="dispatch-booking-hint dispatch-booking-rebook-confirm" role="alert">
                                {t("carrier_booking_rebook_confirm", {
                                    packed: packedLabel,
                                    booked: String(panel.bookedPieces ?? "?"),
                                })}
                            </p>
                        )}
                        <div className="dispatch-booking-actions">
                        {showRetry && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm dispatch-modal-btn dispatch-booking-retry"
                            onClick={onRetry}
                            disabled={retrying || savingAddress}
                        >
                            {retrying
                                ? <Loader2 size={16} className="admin-integrations-spinner" />
                                : <RefreshCw size={14} />}
                            {t("carrier_booking_retry")}
                        </button>
                        )}
                        {/* One primary at a time: the rebook button, then -
                            once clicked - the confirm button in its place
                            with Cancel beside it. */}
                        {isMismatch && onRebook && !confirmingRebook && (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm dispatch-modal-btn dispatch-booking-retry"
                                onClick={() => setConfirmingPanel(panel)}
                                disabled={rebooking}
                            >
                                <RefreshCw size={14} />
                                {t("carrier_booking_rebook", { packed: packedLabel })}
                            </button>
                        )}
                        {isMismatch && onRebook && confirmingRebook && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm dispatch-modal-btn dispatch-booking-retry"
                                    onClick={onRebook}
                                    disabled={rebooking}
                                >
                                    {rebooking
                                        ? <Loader2 size={16} className="admin-integrations-spinner" />
                                        : <RefreshCw size={14} />}
                                    {t("carrier_booking_rebook_confirm_btn")}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm dispatch-modal-btn"
                                    onClick={() => setConfirmingPanel(null)}
                                    disabled={rebooking}
                                >
                                    {t("cancel")}
                                </button>
                            </>
                        )}
                        {isMismatch && onClose && (
                            <button
                                type="button"
                                className="btn btn-secondary btn-sm dispatch-modal-btn"
                                onClick={onClose}
                                disabled={rebooking}
                            >
                                {t("carrier_booking_fix_boxes")}
                            </button>
                        )}
                        {/* Retry is useless for a fatal code — no_match and
                            not_gb fail identically every time, so without
                            this the packer's only options were a button
                            that cannot work and the close button. Flagging
                            hands it to a supervisor, which is the actual
                            resolution. */}
                        {onFlag && (
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm dispatch-modal-btn dispatch-booking-flag"
                                onClick={onFlag}
                                disabled={savingAddress || rebooking}
                            >
                                <Flag size={14} />
                                {t("flag_order")}
                            </button>
                        )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DispatchBookingStatus;
